import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { rdsEvent_Dto, rdsEventStatus_Dto } from "../../interfaces/dtos/rdsEvent";
import { rdsMeeting_Dto } from "../../interfaces/dtos/rdsMeeting";
import { rdsTrack_Dto } from "../../interfaces/dtos/rdsTrack";
import { CommonBaseState, initialCommonBaseState } from "../../interfaces/redux/common";
import { logoutReducers, resetErrors, setError } from "../common/commonActions";
import { RootState } from "../store";
import { loadRdsTracks } from "./rdsActionsTrack";
import { loadRdsMeeting, loadRdsMeetings } from "./rdsActionsMeeting";
import { loadRdsEvent, loadRdsEvents } from "./rdsActionsEvent";
import { loadRdsEventStatuses } from "./rdsActionsEventStatus";
import { rdsSelection_Dto } from "../../interfaces/dtos/rdsSelection";
import { rdsRunner_Dto } from "../../interfaces/dtos/rdsRunner";
import { loadRdsSelections } from "./rdsActionsSelection";
import { loadRdsRunner } from "./rdsActionsRunner";

export interface RdsState extends CommonBaseState {
    loadingRdsTracks: boolean;

    rdsTracks: rdsTrack_Dto[];
    rdsMeetings: rdsMeeting_Dto[];
    rdsEvents: rdsEvent_Dto[];
    rdsEventStatuses: rdsEventStatus_Dto[];
    rdsSelections: rdsSelection_Dto[];
    rdsRunners: rdsRunner_Dto[];
}

const initialState: RdsState = {
    ...initialCommonBaseState,
    loadingRdsTracks: false,

    rdsTracks: [],
    rdsMeetings: [],
    rdsEvents: [],
    rdsEventStatuses: [],
    rdsSelections: [],
    rdsRunners: []
};

export const rdsSlice = createSlice({
    name: 'rds',
    initialState,
    reducers: {
        errorOccured: (state, action: PayloadAction<string>) => {
            state.error = true;
            state.errorMessage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Load rds tracks
            .addCase(loadRdsTracks.pending, (state) => {
                state.loadingRdsTracks = true;
            })
            .addCase(loadRdsTracks.fulfilled, (state, action) => {
                state.loadingRdsTracks = false;
                state.rdsTracks = action.payload;
            })
            .addCase(loadRdsTracks.rejected, (state, action) => {
                state.loadingRdsTracks = false;
                state.error = true;
                state.errorMessage = `Could not load rds tracks:\r\n${action.error.message}`;
            })

            // Load rds meetings
            .addCase(loadRdsMeetings.fulfilled, (state, action) => {
                state.rdsMeetings = action.payload;
            })
            .addCase(loadRdsMeetings.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds meetings:\r\n${action.error.message}`;
            })

            // Load rds meeting
            .addCase(loadRdsMeeting.fulfilled, (state, action) => {
                state.rdsMeetings = action.payload;
            })
            .addCase(loadRdsMeeting.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds meeting:\r\n${action.error.message}`;
            })

            // Load rds events
            .addCase(loadRdsEvents.fulfilled, (state, action) => {
                state.rdsEvents = action.payload;
            })
            .addCase(loadRdsEvents.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds events:\r\n${action.error.message}`;
            })

            // Load rds event
            .addCase(loadRdsEvent.fulfilled, (state, action) => {
                state.rdsEvents = action.payload;
            })
            .addCase(loadRdsEvent.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds event:\r\n${action.error.message}`;
            })

            // Load rds event statuses
            .addCase(loadRdsEventStatuses.fulfilled, (state, action) => {
                state.rdsEventStatuses = action.payload;
            })
            .addCase(loadRdsEventStatuses.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds event statuses:\r\n${action.error.message}`;
            })

            // Load rds selections
            .addCase(loadRdsSelections.fulfilled, (state, action) => {
                state.rdsSelections = action.payload;
            })
            .addCase(loadRdsSelections.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds selections:\r\n${action.error.message}`;
            })

            // Load rds runner
            .addCase(loadRdsRunner.fulfilled, (state, action) => {
                const existingRunners = state.rdsRunners.filter(runner => runner.id !== action.payload.id);
                state.rdsRunners = [...existingRunners, action.payload];
            })
            .addCase(loadRdsRunner.rejected, (state, action) => {
                state.error = true;
                state.errorMessage = `Could not load rds runners:\r\n${action.error.message}`;
            })

            // Set error
            .addCase(setError.fulfilled, (state, action) => {
                state.error = true;
                state.errorMessage = action.payload;
            })
            // Reset error
            .addCase(resetErrors.fulfilled, (state) => {
                state.error = false;
            })
            // Logout
            .addCase(logoutReducers.fulfilled, () => {
                return {...initialState};
            });
    }
});

export const { errorOccured } = rdsSlice.actions;
export const rdsState = (state: RootState) => state.rds;
export default rdsSlice.reducer;
